/**
 * Convert selected PDF pages into a new Craftara Fabric project.
 */

import { doc, setDoc, Timestamp } from 'firebase/firestore';
import type { FabricCanvasData, Project, ProjectPage } from '@canvas/shared-types';
import { projectPath } from '@canvas/shared-types';
import { getClientDB } from '@canvas/firebase-config/client';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import fabric from '@/lib/fabric';
import { createPageId } from '@/lib/projectPages';
import { encodeFirestoreValue } from '@/lib/firestoreSafe';
import { loadAnnotationJson, type FabricJson } from '@/lib/pdf/pdfAnnotations';
import { renderPageCompositeDataUrl } from '@/lib/pdf/pdfDocument';

const IMPORT_SCALE = 1.5;

function path(segments: string) {
  return segments.replace(/^\/+/, '');
}

async function overlayCanvas(
  json: FabricJson | null | undefined,
  width: number,
  height: number
): Promise<HTMLCanvasElement | null> {
  if (!json || !Array.isArray(json.objects) || json.objects.length === 0) return null;
  const el = document.createElement('canvas');
  const canvas = new fabric.Canvas(el, { width, height, selection: false });
  const storedW = Number(json.width) || width;
  const storedH = Number(json.height) || height;
  await loadAnnotationJson(canvas, json);
  if (storedW !== width || storedH !== height) {
    const sx = width / storedW;
    const sy = height / storedH;
    canvas.getObjects().forEach((obj) => {
      obj.scaleX = (obj.scaleX || 1) * sx;
      obj.scaleY = (obj.scaleY || 1) * sy;
      obj.left = (obj.left || 0) * sx;
      obj.top = (obj.top || 0) * sy;
      obj.setCoords();
    });
  }
  canvas.renderAll();
  const out = document.createElement('canvas');
  out.width = width;
  out.height = height;
  out.getContext('2d')?.drawImage(canvas.getElement(), 0, 0);
  canvas.dispose();
  return out;
}

function imageObject(
  dataUrl: string,
  width: number,
  height: number
): Record<string, unknown> {
  return {
    type: 'image',
    version: '5.3.0',
    originX: 'left',
    originY: 'top',
    left: 0,
    top: 0,
    width,
    height,
    scaleX: 1,
    scaleY: 1,
    angle: 0,
    flipX: false,
    flipY: false,
    opacity: 1,
    visible: true,
    backgroundColor: '',
    fill: 'rgb(0,0,0)',
    stroke: null,
    strokeWidth: 0,
    strokeDashArray: null,
    strokeLineCap: 'butt',
    strokeDashOffset: 0,
    strokeLineJoin: 'miter',
    strokeUniform: false,
    strokeMiterLimit: 4,
    shadow: null,
    clipPath: null,
    src: dataUrl,
    crossOrigin: 'anonymous',
    filters: [],
    selectable: true,
    evented: true,
    name: 'pdf-page',
  };
}

function pageCanvasData(
  dataUrl: string,
  width: number,
  height: number
): FabricCanvasData {
  return {
    version: '5.3.0',
    objects: [imageObject(dataUrl, width, height)],
    background: '#ffffff',
    width,
    height,
  };
}

export async function createProjectFromPdf(options: {
  userId: string;
  name: string;
  pdfProxy: PDFDocumentProxy;
  pageAnnotations: Record<number, FabricJson>;
  /** 0-based page indexes; defaults to all */
  pageIndexes?: number[];
  includeAnnotations?: boolean;
}): Promise<Project> {
  const {
    userId,
    name,
    pdfProxy,
    pageAnnotations,
    includeAnnotations = true,
  } = options;

  const indexes =
    options.pageIndexes ??
    Array.from({ length: pdfProxy.numPages }, (_, i) => i);

  const pages: ProjectPage[] = [];

  for (const pageIndex of indexes) {
    const pageNumber = pageIndex + 1;
    const page = await pdfProxy.getPage(pageNumber);
    const base = page.getViewport({ scale: 1 });
    const width = Math.round(base.width * IMPORT_SCALE);
    const height = Math.round(base.height * IMPORT_SCALE);

    let overlay: HTMLCanvasElement | null = null;
    if (includeAnnotations) {
      overlay = await overlayCanvas(pageAnnotations[pageIndex], width, height);
    }

    const { dataUrl } = await renderPageCompositeDataUrl(
      pdfProxy,
      pageNumber,
      IMPORT_SCALE,
      overlay
    );

    pages.push({
      pageId: createPageId(),
      name: `Page ${pageNumber}`,
      canvasData: pageCanvasData(dataUrl, width, height),
    });
  }

  if (!pages.length) throw new Error('No pages to import');

  const first = pages[0];
  const now = new Date();
  const projectId = crypto.randomUUID();

  const project: Project = {
    projectId,
    userId,
    name: name.replace(/\.pdf$/i, '') || 'PDF import',
    description: 'Imported from PDF Studio',
    canvasData: first.canvasData,
    pages,
    activePageId: first.pageId,
    width: first.canvasData.width,
    height: first.canvasData.height,
    tags: ['pdf-import'],
    status: 'draft',
    isPublished: false,
    collaborators: [],
    versionCount: 1,
    fileSize: 0,
    viewCount: 0,
    forkCount: 0,
    commentsEnabled: true,
    usesAI: false,
    createdAt: now,
    lastModifiedAt: now,
    lastModifiedBy: userId,
    updatedAt: now,
    showAuthorOnPublished: true,
    thumbnail: (first.canvasData.objects[0] as { src?: string })?.src,
  };

  const db = getClientDB();
  await setDoc(
    doc(db, path(projectPath(userId, projectId))),
    encodeFirestoreValue({
      ...project,
      createdAt: Timestamp.fromDate(now),
      lastModifiedAt: Timestamp.fromDate(now),
      updatedAt: Timestamp.fromDate(now),
    }) as Record<string, unknown>
  );

  return project;
}
